/**
 * AppLockProvider — OPTIONAL biometric app-lock (Phase 3).
 *
 * ## OPT-IN, OFF BY DEFAULT
 * This provider is a **pass-through** unless ALL of the following hold:
 *   1. the runtime is native (`isTauriRuntime()` — the biometric plugin is
 *      Tauri-mobile only), AND
 *   2. the per-device setting is ON (`isAppLockEnabled()`, default OFF), AND
 *   3. a biometric / device-credential prompt is actually available.
 *
 * When any of those is false — the default for every web/desktop user and for
 * every mobile user who hasn't opted in — the lock state stays `unlocked`, no
 * overlay renders, no prompt fires, and launch is completely unchanged. It NEVER
 * gates individual requests.
 *
 * When armed, it shows a full-screen lock overlay (reusing the boot-overlay
 * styling) on launch and on resume-to-foreground, calls `authenticate()`, and
 * reveals the children only on success. On failure/cancel it stays locked with a
 * Retry button and a "Sign out" escape.
 *
 * Mounted INSIDE the boot gate (`BootManager`), so it guards the app only once
 * it's ready and can reach `useSignOut()` for the escape hatch.
 */

import { msg } from "@lingui/core/macro";
import { createSignal, onCleanup, onMount, type ParentComponent, Show } from "solid-js";
import { Portal } from "solid-js/web";
import { Fingerprint, LogOut } from "lucide-solid";

import {
  type AppLockState,
  authenticateBiometric,
  checkBiometricAvailable,
  createAppLockMachine,
  isAppLockArmed,
} from "@/lib/app-lock";
import { isTauriRuntime } from "@/lib/platform";
import { useI18n } from "@/providers/i18n-provider";
import { useSignOut } from "@/providers/boot-manager";
import { Button } from "@/components/ui/button";
import { GlassSurface } from "@/components/ui/glass-surface";

export const AppLockProvider: ParentComponent = (props) => {
  const { t } = useI18n();
  const { promptSignOut } = useSignOut();

  const [state, setState] = createSignal<AppLockState>("unlocked");

  const machine = createAppLockMachine(
    {
      isArmed: isAppLockArmed,
      checkAvailable: checkBiometricAvailable,
      authenticate: () => authenticateBiometric(t(msg`Unlock to continue`)),
    },
    setState,
  );
  // Seed the signal from the machine's initial state: `locked` when armed (so the
  // overlay is up from first paint), else `unlocked` (the default pass-through).
  setState(machine.state());

  onMount(() => {
    // Non-native (web/desktop-web): pure pass-through. No listeners, no effect.
    if (!isTauriRuntime()) {
      return;
    }

    // Launch evaluation (no-op when not armed — stays unlocked).
    void machine.enforce();

    // Resume-to-foreground: re-lock + re-prompt when armed. `visibilitychange`
    // covers the Android/iOS WebView going background → foreground; `focus`
    // covers window-focus returns. Both short-circuit to unlocked when not armed,
    // so the default OFF path renders nothing.
    const onVisible = (): void => {
      if (document.visibilityState === "visible") {
        void machine.enforce();
      }
    };
    const onFocus = (): void => {
      void machine.enforce();
    };
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", onFocus);
    onCleanup(() => {
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("focus", onFocus);
    });
  });

  return (
    <>
      {props.children}
      <Show when={state() !== "unlocked"}>
        <Portal>
          {/* Opaque full-screen cover (boot-overlay styling) — hides all app
              content behind it while locked. */}
          <div
            class="fixed inset-0 z-[100] flex items-center justify-center bg-background p-4"
            data-testid="app-lock-overlay"
          >
            <GlassSurface class="w-full max-w-sm p-8 flex flex-col items-center text-center space-y-6">
              <div class="flex h-16 w-16 items-center justify-center rounded-full bg-primary/10 text-primary">
                <Fingerprint class="h-8 w-8" />
              </div>
              <div class="space-y-2">
                <h2 class="text-2xl font-bold tracking-tight">{t(msg`App locked`)}</h2>
                <p class="text-sm text-muted-foreground">
                  {t(msg`Verify your identity to continue.`)}
                </p>
              </div>
              <div class="flex w-full flex-col gap-3">
                <Button
                  class="h-11 w-full gap-2 rounded-2xl"
                  loading={state() === "authenticating"}
                  onClick={() => {
                    void machine.retry();
                  }}
                  data-testid="app-lock-retry"
                >
                  <Fingerprint class="h-4 w-4" />
                  {t(msg`Unlock`)}
                </Button>
                <Button
                  variant="ghost"
                  class="h-11 w-full gap-2 rounded-2xl"
                  onClick={() => {
                    promptSignOut();
                  }}
                  data-testid="app-lock-signout"
                >
                  <LogOut class="h-4 w-4" />
                  {t(msg`Sign out`)}
                </Button>
              </div>
            </GlassSurface>
          </div>
        </Portal>
      </Show>
    </>
  );
};
